import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import Footer from "../HomePage/Footer";
import { useMediaQuery } from "react-responsive";
import { API_METHODS, BLANK_VALUE } from "../../../constants";
import useApi from "../../../hooks/useApi";
import { Row, Col, Image, Button, Card, Tooltip, Spin } from "antd";
import { RightOutlined } from "@ant-design/icons";
import { useLocation, useParams } from "react-router-dom";
import { theme } from "../../../assets/theme";
import bannerBg from "../../../assets/img/Herobg.png";
import Breadcrumbs from "../../../components/common/Breadcrumbs";
import { formatBreadcrumbText } from "../../../generalFunctions";

function SubcategoryPage() {
  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 1224px)" });
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const [products, setProducts] = useState([]);
  const [banner, setBanner] = useState();
  const [subcategoryInfo, setSubcategoryInfo] = useState(null);
  const [subcategorySEO, setSubcategorySEO] = useState(null);
  const location = useLocation();
  const { main_category_url, category_url, subcategory_url } = useParams();

  // Infinite scroll states
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const [bannerResponse, getBannerRequest] = useApi(
    `/website/subcategory-page/${subcategory_url}/banner`,
    null,
    API_METHODS.GET
  );

  const [productResponse, getProductRequest] = useApi(
    `/website/subcategory-page/${subcategory_url}/products?pageNumber=1`,
    null,
    API_METHODS.GET
  );

  const productClicked = (product, event) => {
    event.preventDefault();
    event.stopPropagation();
    let url = product.url_slug;
    window.open(location.pathname + `/${url}`, "_blank");
  };

  // Generate SEO data with smart fallbacks
  const generateSEOData = (subcategoryInfo, subcategorySEO) => {
    if (!subcategoryInfo) return {};

    const baseUrl = `https://yourcompany.com/${subcategoryInfo.main_category_url_slug}/${subcategoryInfo.category_url_slug}/${subcategoryInfo.url_slug}`;

    return {
      title:
        subcategorySEO?.meta_title ||
        `${subcategoryInfo.subcategory} - ${subcategoryInfo.category} | Your Company`,

      description:
        subcategorySEO?.meta_description ||
        `Browse our selection of ${subcategoryInfo.subcategory.toLowerCase()} products. Professional ${subcategoryInfo.category.toLowerCase()} equipment for laboratory and research applications.`,

      keywords:
        subcategorySEO?.meta_keywords ||
        `${subcategoryInfo.subcategory}, ${subcategoryInfo.category}, ${subcategoryInfo.main_category}, equipment`,

      h1Tag: subcategorySEO?.h1_tag || subcategoryInfo.subcategory,

      canonicalUrl: subcategorySEO?.canonical_url || baseUrl,

      robots: subcategorySEO?.meta_robots || "index, follow",

      ogTitle:
        subcategorySEO?.og_title ||
        subcategorySEO?.meta_title ||
        subcategoryInfo.subcategory,

      ogDescription:
        subcategorySEO?.og_description ||
        subcategorySEO?.meta_description ||
        `Shop ${subcategoryInfo.subcategory} products from Your Company`,

      ogImage: subcategorySEO?.og_image || "/default-subcategory-og-image.jpg",

      twitterTitle:
        subcategorySEO?.twitter_title ||
        subcategorySEO?.og_title ||
        subcategoryInfo.subcategory,

      twitterDescription:
        subcategorySEO?.twitter_description ||
        subcategorySEO?.og_description ||
        `Discover ${subcategoryInfo.subcategory} products`,

      twitterImage:
        subcategorySEO?.twitter_image ||
        subcategorySEO?.og_image ||
        "/default-subcategory-og-image.jpg",

      breadcrumbName:
        subcategorySEO?.breadcrumb_name || subcategoryInfo.subcategory,
    };
  };

  // Generate structured data for subcategory
  const generateSubcategorySchema = (subcategoryInfo, seoData) => {
    if (!subcategoryInfo) return null;

    const baseSchema = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: seoData.title,
      description: seoData.description,
      url: seoData.canonicalUrl,
      breadcrumb: {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://yourcompany.com",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: subcategoryInfo.main_category,
            item: `https://yourcompany.com/${subcategoryInfo.main_category_url_slug}`,
          },
          {
            "@type": "ListItem",
            position: 3,
            name: subcategoryInfo.category,
            item: `https://yourcompany.com/${subcategoryInfo.main_category_url_slug}/${subcategoryInfo.category_url_slug}`,
          },
          {
            "@type": "ListItem",
            position: 4,
            name: seoData.breadcrumbName,
          },
        ],
      },
    };

    // Merge with additional schema if provided
    if (subcategorySEO?.additional_schema) {
      return { ...baseSchema, ...subcategorySEO.additional_schema };
    }

    return baseSchema;
  };

  // Scroll detection for infinite scroll
  useEffect(() => {
    const handleScroll = () => {
      // More generous scroll detection - trigger when 70% scrolled
      const scrollTop = document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = document.documentElement.clientHeight;

      // Calculate scroll percentage
      const scrollPercentage =
        (scrollTop / (scrollHeight - clientHeight)) * 100;
      const shouldLoadMore = scrollPercentage >= 70; // Trigger when 70% scrolled

      if (
        shouldLoadMore &&
        hasMore &&
        !loadingMore &&
        !productResponse.isLoading
      ) {
        setLoadingMore(true);
        setCurrentPage((prev) => prev + 1);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [hasMore, loadingMore, productResponse.isLoading, currentPage]);

  // Load more products when currentPage changes
  useEffect(() => {
    if (currentPage > 1 && subcategory_url) {
      getProductRequest(
        "",
        API_METHODS.GET,
        `/website/subcategory-page/${subcategory_url}/products?pageNumber=${currentPage}`
      );
    }
  }, [currentPage, subcategory_url]);

  useEffect(() => {
    // Reset pagination when subcategory changes
    setCurrentPage(1);
    setProducts([]);
    setHasMore(true);
    setLoadingMore(false);

    if (subcategory_url) {
      getProductRequest(
        "",
        API_METHODS.GET,
        `/website/subcategory-page/${subcategory_url}/products?pageNumber=1`
      );
    }
  }, [subcategory_url]);

  useEffect(() => {
    getBannerRequest();
  }, []);

  // Handle enhanced product response with SEO data
  // Handle enhanced product response with SEO data
  useEffect(() => {
    if (productResponse.data !== BLANK_VALUE && productResponse.data) {
      const responseData = productResponse.data;

      // Set subcategory info and SEO data
      if (responseData.subcategory_info) {
        setSubcategoryInfo(responseData.subcategory_info);
      }
      if (responseData.subcategory_seo) {
        setSubcategorySEO(responseData.subcategory_seo);
      }

      // Handle products data
      const newData = Array.isArray(responseData.products)
        ? responseData.products
        : [];
      const pagination =
        responseData.pagination !== BLANK_VALUE
          ? responseData.pagination
          : null;

      if (currentPage === 1) {
        setProducts(newData);
      } else if (newData.length > 0) {
        setProducts((prev) => {
          const prevArray = Array.isArray(prev) ? prev : [];
          const newArray = Array.isArray(newData) ? newData : [];
          const existingIds = new Set(prevArray.map((item) => item.id));
          const uniqueNewData = newArray.filter(
            (item) => !existingIds.has(item.id)
          );
          return [...prevArray, ...uniqueNewData];
        });
      }

      // Update hasMore based on pagination info
      if (pagination && pagination.maxPages) {
        const maxPages = parseInt(pagination.maxPages) || 1;
        setHasMore(currentPage < maxPages);
      } else {
        setHasMore(false);
      }
      setLoadingMore(false);
    }
  }, [productResponse, currentPage]);

  useEffect(() => {
    const data = bannerResponse.data !== BLANK_VALUE ? bannerResponse.data : [];
    setBanner(data);
  }, [bannerResponse]);

  // Generate SEO data
  const seoData = generateSEOData(subcategoryInfo, subcategorySEO);
  const schemaData = generateSubcategorySchema(subcategoryInfo, seoData);
  // Mobile-responsive styles
  const bannerContainerStyle = {
    backgroundImage: `url(${bannerBg})`,
    backgroundSize: "cover",
    padding: isMobile ? "20px 16px" : isTabletOrMobile ? "30px 24px" : "40px",
    marginBottom: isMobile ? "16px" : "24px",
    margin: isMobile ? "0 8px 16px 8px" : "0 0 24px 0",
  };

  const bannerTitleStyle = {
    fontSize: isMobile ? "28px" : isTabletOrMobile ? "36px" : "48px",
    fontWeight: 700,
    color: "#333",
    marginBottom: "16px",
    lineHeight: isMobile ? "1.2" : "1.3",
  };

  const bannerTextStyle = {
    color: "#666",
    lineHeight: 1.6,
    fontSize: isMobile ? "14px" : "16px",
  };

  const cardContainerStyle = {
    minHeight: isMobile ? "auto" : "100vh",
    padding: isMobile ? "16px 8px" : isTabletOrMobile ? "30px 20px" : "60px",
  };

  const productImageStyle = {
    height: isMobile ? 200 : isTabletOrMobile ? 250 : 300,
    width: "100%",
    objectFit: "contain",
  };

  const cardMetaStyle = {
    minHeight: isMobile ? "140px" : "170px",
  };

  const buttonStyle = {
    borderRadius: 50,
    fontWeight: 500,
    marginTop: 16,
    color: theme.colors.skyBlue,
    borderColor: theme.colors.skyBlue,
    height: isMobile ? "40px" : "auto",
    fontSize: isMobile ? "14px" : "16px",
    padding: 6,
  };

  return (
    <div>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        {seoData.keywords && (
          <meta name="keywords" content={seoData.keywords} />
        )}
        <meta name="robots" content={seoData.robots} />

        {/* Canonical URL */}
        <link rel="canonical" href={seoData.canonicalUrl} />

        {/* Open Graph Tags */}
        <meta property="og:title" content={seoData.ogTitle} />
        <meta property="og:description" content={seoData.ogDescription} />
        <meta property="og:image" content={seoData.ogImage} />
        <meta property="og:url" content={seoData.canonicalUrl} />
        <meta property="og:type" content="website" />

        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoData.twitterTitle} />
        <meta name="twitter:description" content={seoData.twitterDescription} />
        <meta name="twitter:image" content={seoData.twitterImage} />

        {/* Structured Data */}
        {schemaData && (
          <script type="application/ld+json">
            {JSON.stringify(schemaData)}
          </script>
        )}
      </Helmet>

      {banner && (
        <>
          <div style={bannerContainerStyle}>
            <Row
              align={isMobile ? "top" : "middle"}
              justify="space-between"
              gutter={[16, 16]}
            >
              <Col xs={24} sm={24} md={14} lg={16} order={isMobile ? 2 : 1}>
                <h1 style={bannerTitleStyle}>{banner.title}</h1>
                <p style={bannerTextStyle}>
                  <b>{banner.banner_text}</b>
                </p>
              </Col>

              <Col
                xs={24}
                sm={24}
                md={10}
                lg={8}
                className="banner-image-col"
                order={isMobile ? 1 : 2}
                style={{
                  textAlign: isMobile ? "center" : "right",
                }}
              >
                <Image
                  src={banner.image}
                  preview={false}
                  style={{
                    maxWidth: isMobile ? "80%" : "100%",
                    height: "auto",
                    borderRadius: "8px",
                    maxHeight: isMobile ? "150px" : "400px",
                    objectFit: "cover",
                    paddingRight: !isMobile && 60,
                  }}
                  alt={banner.title}
                />
              </Col>
            </Row>
          </div>

          <Breadcrumbs
            items={[
              {
                title: formatBreadcrumbText(main_category_url),
                href: `/${main_category_url}/${category_url}`,
              },
              {
                title: formatBreadcrumbText(category_url),
                href: `/${main_category_url}/${category_url}`,
              },
              {
                title: formatBreadcrumbText(subcategory_url),
              },
            ]}
          />
        </>
      )}

      <div style={cardContainerStyle}>
        <Row gutter={isMobile ? [8, 16] : [16, 16]}>
          {products?.map((product, index) => (
            <Col
              xs={24}
              sm={12}
              md={8}
              lg={8}
              xl={6}
              key={product.id} // Use unique ID instead of index-based key
            >
              <Card
                hoverable
                onClick={(event) => productClicked(product, event)}
                style={{
                  cursor: "pointer",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
                bodyStyle={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  padding: isMobile ? "12px" : "24px",
                }}
                cover={
                  <div
                    style={{ overflow: "hidden", height: isMobile ? 200 : 300 }}
                  >
                    <img
                      src={product.image_path}
                      style={productImageStyle}
                      alt={product.product_name}
                    />
                  </div>
                }
              >
                <div
                  style={{ flex: 1, display: "flex", flexDirection: "column" }}
                >
                  <Card.Meta
                    style={cardMetaStyle}
                    title={
                      <Tooltip title={product.product_name}>
                        <div
                          style={{
                            fontSize: isMobile ? "14px" : "16px",
                            fontWeight: "600",
                            lineHeight: "1.4",
                            marginBottom: "8px",
                          }}
                        >
                          {product.product_name}
                        </div>
                      </Tooltip>
                    }
                    description={
                      <div
                        style={{
                          fontSize: isMobile ? "12px" : "14px",
                          color: "#666",
                          lineHeight: "1.4",
                        }}
                      >
                        {product.products_spec?.map((spec, index) => (
                          <div
                            key={spec.specification_order}
                            style={{ marginBottom: "4px" }}
                          >
                            <span>
                              {spec.parameter}: {spec.specifications}
                            </span>
                          </div>
                        ))}
                      </div>
                    }
                  />

                  <Button
                    block
                    type="default"
                    style={buttonStyle}
                    onClick={(event) => productClicked(product, event)}
                  >
                    View Details
                    <RightOutlined />
                  </Button>
                </div>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Loading indicator for infinite scroll */}
        {loadingMore && (
          <div
            style={{
              textAlign: "center",
              padding: "20px",
              marginTop: "16px",
            }}
          >
            <Spin size="large" />
            <p style={{ marginTop: "8px", color: "#666" }}>
              Loading more products...
            </p>
          </div>
        )}

        {/* Empty state for products */}
        {products && products.length === 0 && !productResponse.isLoading && (
          <div
            style={{
              textAlign: "center",
              padding: "40px 20px",
              color: "#666",
            }}
          >
            <p>No products found for this subcategory.</p>
          </div>
        )}
      </div>

      <Footer isTabletOrMobile={isTabletOrMobile} />
    </div>
  );
}

export default SubcategoryPage;
